import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation, useOutletContext } from 'react-router-dom';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
    FaCheckCircle, FaChevronLeft, FaChevronRight, FaChevronDown,
    FaBars, FaTimes, FaBookOpen, FaPlayCircle, FaFileAlt, FaFilePdf, FaGlobe,
    FaLock, FaRocket, FaStar, FaArrowLeft
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import serverURL from '../../config';
import Quiz from './Quiz';

/* ─── Circular Progress Ring ─────────────────────────────────────────────── */
const CircularProgress = ({ percentage }) => {
    const radius = 36;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (percentage / 100) * circumference;
    return (
        <svg width="88" height="88" viewBox="0 0 88 88" className="rotate-[-90deg]">
            <circle cx="44" cy="44" r={radius} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="7" />
            <circle
                cx="44" cy="44" r={radius}
                fill="none"
                stroke="url(#progressGrad)"
                strokeWidth="7"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                style={{ transition: 'stroke-dashoffset 1s cubic-bezier(0.22,1,0.36,1)' }}
            />
            <defs>
                <linearGradient id="progressGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#f87171" />
                    <stop offset="100%" stopColor="#10b981" />
                </linearGradient>
            </defs>
        </svg>
    );
};
CircularProgress.propTypes = {
    percentage: PropTypes.number.isRequired,
};

/* ─── Content Type Badge ─────────────────────────────────────────────────── */
const ContentTypeBadge = ({ type }) => {
    const map = {
        video: { label: 'Video', bg: 'bg-blue-50', text: 'text-blue-600', icon: <FaPlayCircle size={10} /> },
        pdf: { label: 'PDF', bg: 'bg-red-50', text: 'text-red-600', icon: <FaFilePdf size={10} /> },
        iframe: { label: 'Web', bg: 'bg-purple-50', text: 'text-purple-600', icon: <FaGlobe size={10} /> },
        quiz: { label: 'Quiz', bg: 'bg-amber-50', text: 'text-amber-600', icon: <FaStar size={10} /> },
        markdown: { label: 'Article', bg: 'bg-teal-50', text: 'text-teal-600', icon: <FaFileAlt size={10} /> },
        html: { label: 'Article', bg: 'bg-teal-50', text: 'text-teal-600', icon: <FaFileAlt size={10} /> },
        text: { label: 'Reading', bg: 'bg-gray-100', text: 'text-gray-600', icon: <FaFileAlt size={10} /> },
    };
    const cfg = map[type?.toLowerCase()] || map.text;
    return (
        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${cfg.bg} ${cfg.text}`}>
            {cfg.icon}{cfg.label}
        </span>
    );
};
ContentTypeBadge.propTypes = { type: PropTypes.string };
ContentTypeBadge.defaultProps = { type: 'text' };

/* ─── Skeleton Loader ────────────────────────────────────────────────────── */
const SkeletonLoader = () => (
    <div className="flex h-screen bg-gray-50 flex-col md:flex-row overflow-hidden">
        <div className="hidden md:flex md:w-64 lg:w-72 xl:w-80 flex-col bg-white border-r border-gray-100">
            <div className="p-5 bg-gray-900 h-48 flex flex-col items-center justify-center gap-3">
                <div className="skeleton-shimmer w-20 h-20 rounded-full" style={{ background: 'rgba(255,255,255,0.1)' }} />
                <div className="skeleton-shimmer w-32 h-3 rounded" style={{ background: 'rgba(255,255,255,0.1)' }} />
                <div className="skeleton-shimmer w-24 h-2 rounded" style={{ background: 'rgba(255,255,255,0.08)' }} />
            </div>
            <div className="flex-1 p-4 space-y-3">
                {[1, 2, 3, 4].map(i => (
                    <div key={i} className="rounded-xl border border-gray-100 p-4 space-y-2">
                        <div className="skeleton-shimmer h-3 w-3/4 rounded" />
                        <div className="skeleton-shimmer h-2 w-1/2 rounded" />
                        <div className="skeleton-shimmer h-1 w-full rounded-full mt-2" />
                    </div>
                ))}
            </div>
        </div>
        <div className="flex-1 flex flex-col p-8 space-y-6">
            <div className="skeleton-shimmer h-4 w-32 rounded" />
            <div className="skeleton-shimmer h-8 w-2/3 rounded" />
            <div className="skeleton-shimmer h-4 w-full rounded" />
            <div className="skeleton-shimmer h-4 w-5/6 rounded" />
            <div className="skeleton-shimmer h-4 w-4/6 rounded" />
            <div className="skeleton-shimmer h-64 w-full rounded-xl mt-4" />
        </div>
    </div>
);

/* ─── Helpers ────────────────────────────────────────────────────────────── */
const getSectionBorderClass = (isSectionCompleted, isActiveSection) => {
    if (isSectionCompleted) return 'border-green-200 bg-white shadow-sm';
    if (isActiveSection) return 'border-red-200 bg-white shadow-md ring-1 ring-red-100';
    return 'border-gray-100 bg-white shadow-sm hover:shadow-md hover:border-gray-200';
};

const getSectionTitleClass = (showSectionCheck, isActiveSection) => {
    if (showSectionCheck) return 'text-green-800';
    if (isActiveSection) return 'text-gray-900';
    return 'text-gray-700';
};

const getProgressBarClass = (progressPercent) => {
    if (progressPercent === 100) return 'bg-gradient-to-r from-green-400 to-green-500';
    if (progressPercent > 0) return 'bg-gradient-to-r from-orange-400 to-orange-500';
    return 'bg-transparent';
};

const getDotClass = (isCurrentDot, isDone) => {
    if (isCurrentDot) return 'w-5 h-2 bg-red-500';
    if (isDone) return 'w-2 h-2 bg-green-400';
    return 'w-2 h-2 bg-gray-200 hover:bg-gray-300';
};

const getNextButtonClass = (isMarkingComplete, isSectionLastItem, currentSectionStatus, isLastItem) => {
    if (isMarkingComplete) return 'bg-gray-400 text-white cursor-wait';
    if (isSectionLastItem && currentSectionStatus !== 'completed')
        return 'bg-gradient-to-r from-green-500 to-green-500 text-white hover:from-green-600 hover:to-emerald-600 hover:shadow-green-300/40 hover:shadow-lg';
    if (isLastItem && currentSectionStatus === 'completed')
        return 'bg-gray-100 text-gray-400 cursor-not-allowed shadow-none border border-gray-200';
    return 'bg-gradient-to-r from-gray-900 to-gray-800 text-white hover:from-red-600 hover:to-red-700 hover:shadow-red-300/40 hover:shadow-lg';
};

const getSectionStatusBadge = (isSectionCompleted, isActiveSection) => {
    if (isSectionCompleted) return { label: 'Complete', cls: 'bg-green-100 text-green-700', icon: <FaCheckCircle size={8} /> };
    if (isActiveSection) return { label: 'In Progress', cls: 'bg-orange-100 text-orange-500', icon: <FaRocket size={8} /> };
    return { label: 'Not Started', cls: 'bg-gray-100 text-gray-500', icon: <FaLock size={8} /> };
};

const getEmbedUrl = (url) => {
    if (!url) return url;
    const youtubeRegex = /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(youtubeRegex);
    return match?.[1] ? `https://www.youtube.com/embed/${match[1]}` : url;
};

const buildHtmlDocument = (content) => `<!DOCTYPE html><html><head><style>body { font-family: ui-sans-serif, system-ui, -apple-system, sans-serif; line-height: 1.6; color: #374151; padding: 1.5rem; margin: 0; word-wrap: break-word; } img { max-width: 100%; height: auto; }</style></head><body>${content}</body></html>`;

const getItemIcon = (item, isActive, isCompleted) => {
    if (isCompleted) return <FaCheckCircle className="animate-scale-in text-green-500" size={13} />;
    const type = item.content_type?.toLowerCase();
    const color = isActive ? 'text-red-500' : 'text-gray-300';
    if (type === 'quiz') return <FaStar size={13} className={color} />;
    if (type === 'video') return <FaPlayCircle size={13} className={color} />;
    if (type === 'pdf') return <FaFilePdf size={13} className={color} />;
    if (type === 'iframe') return <FaGlobe size={13} className={color} />;
    return <FaFileAlt size={13} className={color} />;
};

/* ─── Next Button Content Helper ────────────────────────────────────────── */
const getNextButtonContent = (isMarkingComplete, isSectionLastItem, currentSectionStatus) => {
    if (isMarkingComplete) {
        return (
            <span className="flex items-center gap-2">
                <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                Saving...
            </span>
        );
    }
    if (isSectionLastItem && currentSectionStatus !== 'completed') {
        return (
            <span className="flex items-center gap-2">
                Complete <FaCheckCircle size={12} />
            </span>
        );
    }
    return (
        <span className="flex items-center gap-2">
            <span className="hidden sm:inline">Next</span>
            <FaChevronRight size={10} />
        </span>
    );
};

/* ─── Initial Selection Resolver ────────────────────────────────────────── */
const resolveInitialSelection = (fetchedSections, initialLevelId) => {
    if (initialLevelId) {
        const sectionIndex = fetchedSections.findIndex(
            l => l.level_id === parseInt(initialLevelId)
        );
        if (sectionIndex !== -1 && fetchedSections[sectionIndex].items?.length > 0) {
            return { sectionIndex, itemIndex: 0 };
        }
        return null;
    }
    const firstIncompleteIdx = fetchedSections.findIndex(s => s.status !== 'completed');
    if (firstIncompleteIdx !== -1 && fetchedSections[firstIncompleteIdx].items?.length > 0) {
        return { sectionIndex: firstIncompleteIdx, itemIndex: 0 };
    }
    if (fetchedSections.length > 0 && fetchedSections[0].items?.length > 0) {
        return { sectionIndex: 0, itemIndex: 0 };
    }
    return null;
};

/* ─── Completed Items Builder ───────────────────────────────────────────── */
const buildInitialCompletedItems = (fetchedSections) => {
    const initialCompletedItems = new Set();
    fetchedSections.forEach((section, idx) => {
        if (section.status === 'completed' && section.items) {
            section.items.forEach(item =>
                initialCompletedItems.add(item.id || `${idx}-${item.title}`)
            );
        }
    });
    return initialCompletedItems;
};

/* ─── Navigate Next (extracted to reduce LessonViewer cognitive complexity) */
const navigateToNext = (currentSelection, sections, setCurrentSelection, setExpandedSections) => {
    const { sectionIndex, itemIndex } = currentSelection;
    const currentSec = sections[sectionIndex];
    if (itemIndex < currentSec.items.length - 1) {
        setCurrentSelection({ sectionIndex, itemIndex: itemIndex + 1 });
        return;
    }
    if (sectionIndex >= sections.length - 1) return;
    let nextSecIdx = sectionIndex + 1;
    while (nextSecIdx < sections.length && (!sections[nextSecIdx].items || sections[nextSecIdx].items.length === 0)) {
        nextSecIdx++;
    }
    if (nextSecIdx < sections.length) {
        setCurrentSelection({ sectionIndex: nextSecIdx, itemIndex: 0 });
        setExpandedSections(prev => ({ ...prev, [nextSecIdx]: true }));
        setTimeout(() => {
            document.getElementById(`section-${nextSecIdx}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 100);
    }
};

/* ─── Navigate Previous (extracted to reduce LessonViewer cognitive complexity) */
const navigateToPrevious = (currentSelection, sections, setCurrentSelection, setExpandedSections) => {
    const { sectionIndex, itemIndex } = currentSelection;
    if (itemIndex > 0) {
        setCurrentSelection({ sectionIndex, itemIndex: itemIndex - 1 });
        return;
    }
    if (sectionIndex <= 0) return;
    let prevSecIdx = sectionIndex - 1;
    while (prevSecIdx >= 0 && (!sections[prevSecIdx].items || sections[prevSecIdx].items.length === 0)) {
        prevSecIdx--;
    }
    if (prevSecIdx >= 0) {
        const prevSec = sections[prevSecIdx];
        setCurrentSelection({ sectionIndex: prevSecIdx, itemIndex: prevSec.items.length - 1 });
        setExpandedSections(prev => ({ ...prev, [prevSecIdx]: true }));
    }
};

/* ─── Section Item ──────────────────────────────────────────────────────── */
const SectionItem = ({ item, sectionIdx, itemIdx, isActive, isItemCompleted, onSelect }) => (
    <button
        type="button"
        onClick={() => onSelect(sectionIdx, itemIdx)}
        className={`
            w-full text-left px-4 py-2.5 flex items-center gap-3 cursor-pointer
            border-l-[3px] transition-all duration-150 group
            ${isActive
                ? 'border-red-500 bg-red-50/70 text-gray-900'
                : 'border-transparent hover:bg-gray-50 hover:border-gray-200 text-gray-600 hover:text-gray-800'}
        `}
    >
        <div className="flex-shrink-0 w-5 flex items-center justify-center">
            {getItemIcon(item, isActive, isItemCompleted)}
        </div>
        <span className={`text-xs leading-snug line-clamp-2 flex-1 ${isActive ? 'font-semibold text-gray-900' : 'font-normal'}`}>
            {item.title}
        </span>
        {isActive && (
            <FaChevronRight size={8} className="flex-shrink-0 text-red-400 opacity-0 group-hover:opacity-100 transition-opacity" />
        )}
    </button>
);
SectionItem.propTypes = {
    item: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        title: PropTypes.string,
        content_type: PropTypes.string,
    }).isRequired,
    sectionIdx: PropTypes.number.isRequired,
    itemIdx: PropTypes.number.isRequired,
    isActive: PropTypes.bool.isRequired,
    isItemCompleted: PropTypes.bool.isRequired,
    onSelect: PropTypes.func.isRequired,
};

/* ─── Section Row ───────────────────────────────────────────────────────── */
const SectionRow = ({ section, sectionIdx, currentSelection, completedItems, expandedSections, onToggle, onSelect }) => {
    const isSectionCompleted = section.status === 'completed';
    const isExpanded = expandedSections[sectionIdx];
    const hasItems = section.items && section.items.length > 0;
    const isActiveSection = currentSelection.sectionIndex === sectionIdx;

    let completedCountInSection = 0;
    if (section.items) {
        section.items.forEach(item => {
            const itemId = item.id || `${sectionIdx}-${item.title}`;
            if (completedItems.has(itemId)) completedCountInSection++;
        });
    }
    const totalItems = section.items?.length || 0;

    // Extracted nested ternary into independent statement
    const completedFallback = isSectionCompleted ? 100 : 0;
    const progressPercent = totalItems > 0
        ? Math.round((completedCountInSection / totalItems) * 100)
        : completedFallback;

    const showSectionCheck = isSectionCompleted || progressPercent === 100;
    const statusBadge = getSectionStatusBadge(isSectionCompleted, isActiveSection);

    return (
        <div
            id={`section-${sectionIdx}`}
            className={`mb-2 mx-3 rounded-xl overflow-hidden border transition-all duration-200 ${getSectionBorderClass(isSectionCompleted, isActiveSection)}`}
        >
            <button
                type="button"
                onClick={() => onToggle(sectionIdx)}
                className="w-full text-left p-4 cursor-pointer flex flex-col transition-colors hover:bg-gray-50/60 select-none"
                aria-expanded={isExpanded}
            >
                <div className="flex justify-between items-start mb-2">
                    <div className="flex-1 pr-2 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold ${statusBadge.cls}`}>
                                {statusBadge.icon} {statusBadge.label}
                            </span>
                            <h3 className={`text-sm font-bold truncate ${getSectionTitleClass(showSectionCheck, isActiveSection)}`}>
                                {section.level_name}
                            </h3>
                        </div>
                    </div>
                    <div className={`flex-shrink-0 mt-0.5 transition-transform duration-200 ${isExpanded ? 'rotate-180' : 'rotate-0'}`}>
                        <FaChevronDown size={10} className="text-gray-400" />
                    </div>
                </div>
                <div className="flex items-center gap-2 mt-2">
                    {/* <div className={`flex-shrink-0 transition-all duration-300 ${showSectionCheck ? 'text-green-500' : 'text-gray-200'}`}>
                        <FaCheckCircle size={20} />
                    </div> */}
                    <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden relative">
                        <div
                            className={`h-full rounded-full transition-all duration-700 ease-out ${getProgressBarClass(progressPercent)}`}
                            style={{ width: `${progressPercent}%` }}
                        />
                        {progressPercent > 0 && (
                            <span className="absolute inset-0 flex items-center justify-center text-[5px] font-bold text-white leading-none pointer-events-none tracking-wide">

                            </span>
                        )}
                    </div>
                    <span className="text-[10px] text-gray-400 font-medium">{completedCountInSection}/{totalItems}</span>
                </div>
            </button>
            {isExpanded && hasItems && (
                <div className={`border-t ${isSectionCompleted ? 'border-green-100' : 'border-gray-100'}`}>
                    {section.items.map((item, itemIdx) => {
                        const isActive = currentSelection.sectionIndex === sectionIdx && currentSelection.itemIndex === itemIdx;
                        const itemId = item.id || `${sectionIdx}-${item.title}`;
                        const isItemCompleted = completedItems.has(itemId) || isSectionCompleted;
                        return (
                            <SectionItem
                                key={item.id || `${sectionIdx}-${itemIdx}`}
                                item={item}
                                sectionIdx={sectionIdx}
                                itemIdx={itemIdx}
                                isActive={isActive}
                                isItemCompleted={isItemCompleted}
                                onSelect={onSelect}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
};
SectionRow.propTypes = {
    section: PropTypes.shape({
        level_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        level_name: PropTypes.string,
        status: PropTypes.string,
        items: PropTypes.array,
    }).isRequired,
    sectionIdx: PropTypes.number.isRequired,
    currentSelection: PropTypes.shape({
        sectionIndex: PropTypes.number,
        itemIndex: PropTypes.number,
    }).isRequired,
    completedItems: PropTypes.instanceOf(Set).isRequired,
    expandedSections: PropTypes.object.isRequired,
    onToggle: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
};

/* ─── Embed Content ──────────────────────────────────────────────────────── */
const EmbedContent = ({ currentItem, currentSection, currentSelection }) => {
    const type = currentItem.content_type?.toLowerCase();
    return (
        <div className="flex flex-col flex-1 min-h-0">
            <div className="w-full animate-fade-slide px-5 md:px-10 pt-8 md:pt-12 pb-4">
                <div className="mb-4">
                    <div className="flex items-center gap-2 mb-4">
                        <span className="text-[10px] font-bold text-red-500 uppercase tracking-widest">
                            {currentSection.level_name}
                        </span>
                        <span className="text-gray-200 text-sm">›</span>
                        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                            Lesson {currentSelection.itemIndex + 1}
                        </span>
                        <span className="ml-1">
                            <ContentTypeBadge type={currentItem.content_type} />
                        </span>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-black text-gray-900 leading-tight mb-4">
                        {currentItem.title}
                    </h2>
                    <div className="flex items-center gap-3">
                        <div className="h-0.5 w-10 rounded-full bg-red-500" />
                        <div className="h-0.5 flex-1 rounded-full bg-gray-100" />
                    </div>
                </div>
            </div>
            <div className="flex-1 min-h-0 w-full px-5 md:px-10 pb-5" style={{ paddingBottom: '88px' }}>
                <div className="w-full h-full rounded-2xl overflow-hidden border border-gray-200 shadow-sm bg-black">
                    {type === 'video' && (
                        <iframe
                            key={currentItem.content}
                            src={getEmbedUrl(currentItem.content)}
                            className="w-full h-full border-0"
                            sandbox="allow-same-origin allow-scripts allow-presentation"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                            allowFullScreen
                            title={currentItem.title}
                        />
                    )}
                    {type === 'pdf' && (
                        <iframe
                            key={currentItem.content}
                            src={currentItem.content}
                            className="w-full h-full border-0 bg-white"
                            sandbox="allow-same-origin allow-scripts"
                            title="PDF Document"
                        />
                    )}
                    {type === 'iframe' && (
                        <iframe
                            key={currentItem.content}
                            src={getEmbedUrl(currentItem.content)}
                            className="w-full h-full border-0 bg-white"
                            sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-presentation"
                            title={currentItem.title}
                            allowFullScreen
                        />
                    )}
                    {type === 'html' && (
                        <iframe
                            key={currentItem.content}
                            srcDoc={buildHtmlDocument(currentItem.content)}
                            className="w-full h-full border-0 bg-white"
                            sandbox=""
                            title={currentItem.title}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};
EmbedContent.propTypes = {
    currentItem: PropTypes.object.isRequired,
    currentSection: PropTypes.object.isRequired,
    currentSelection: PropTypes.object.isRequired,
};

/* ─── Inline Content ─────────────────────────────────────────────────────── */
const InlineContent = ({ currentItem, currentSection, currentSelection, contentKey, contentScrollRef, handleQuizCompletion }) => {
    const isQuiz = currentItem.content_type?.toLowerCase() === 'quiz';
    const type = currentItem.content_type?.toLowerCase();
    return (
        <div
            ref={contentScrollRef}
            className="flex-1 overflow-y-auto custom-scrollbar"
            style={{ paddingBottom: isQuiz ? '0' : '88px' }}
        >
            <div key={contentKey} className={`w-full animate-fade-slide ${isQuiz ? '' : 'px-5 md:px-10 py-8 md:py-12'}`}>
                {!isQuiz && (
                    <div className="mb-8">
                        <div className="flex items-center gap-2 mb-4">
                            <span className="text-[10px] font-bold text-red-500 uppercase tracking-widest">
                                {currentSection.level_name}
                            </span>
                            <span className="text-gray-200 text-sm">›</span>
                            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                                Lesson {currentSelection.itemIndex + 1}
                            </span>
                            <span className="ml-1">
                                <ContentTypeBadge type={currentItem.content_type} />
                            </span>
                        </div>
                        <h2 className="text-2xl md:text-3xl font-black text-gray-900 leading-tight mb-4">
                            {currentItem.title}
                        </h2>
                        <div className="flex items-center gap-3">
                            <div className="h-0.5 w-10 rounded-full bg-red-500" />
                            <div className="h-0.5 flex-1 rounded-full bg-gray-100" />
                        </div>
                    </div>
                )}
                <div className="lesson-content">
                    {type === 'quiz' && (
                        <Quiz embedded={true} levelId={currentItem.level_id} onComplete={handleQuizCompletion} />
                    )}
                    {type === 'markdown' && (
                        <div className="prose prose-lg prose-red max-w-none prose-headings:font-black prose-p:text-gray-700 prose-p:leading-relaxed">
                            <ReactMarkdown>{currentItem.content}</ReactMarkdown>
                        </div>
                    )}
                    {/* HTML content is handled via EmbedContent iframe to isolate layout styles */}
                    {type === 'text' && (
                        <div className="whitespace-pre-wrap text-gray-700 leading-relaxed text-base font-normal">
                            {currentItem.content}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
InlineContent.propTypes = {
    currentItem: PropTypes.object.isRequired,
    currentSection: PropTypes.object.isRequired,
    currentSelection: PropTypes.object.isRequired,
    contentKey: PropTypes.number.isRequired,
    contentScrollRef: PropTypes.object.isRequired,
    handleQuizCompletion: PropTypes.func.isRequired,
};

/* ─── Bottom Nav Bar ─────────────────────────────────────────────────────── */
const BottomNavBar = ({
    currentSection, currentSelection, completedItems,
    totalItemsInSection, isFirstItem, isLastItem, isSectionLastItem,
    isMarkingComplete, handlePrevious, handleLessonSelect, handleCompleteAndContinue,
}) => {
    const maxDots = 7;
    const dotsToShow = Math.min(totalItemsInSection, maxDots);
    return (
        <div className="absolute bottom-0 left-0 right-0 z-10 border-t border-gray-100 bg-white px-4 py-3 shadow-[0_-8px_32px_-8px_rgba(0,0,0,0.08)]">
            <div className="max-w-3xl mx-auto flex justify-between items-center gap-4">
                <button
                    onClick={handlePrevious}
                    disabled={isFirstItem}
                    className={`
                        px-4 py-2.5 rounded-full font-semibold text-sm flex items-center gap-2 transition-all duration-200
                        ${isFirstItem
                            ? 'text-gray-300 cursor-not-allowed bg-gray-50 border border-gray-100'
                            : 'text-gray-600 bg-white border border-gray-200 hover:border-red-200 hover:text-red-600 hover:bg-red-50 shadow-sm active:scale-95'}
                    `}
                >
                    <FaChevronLeft size={10} />
                    <span className="hidden sm:inline">Previous</span>
                </button>

                <div className="flex items-center gap-1.5">
                    {Array.from({ length: dotsToShow }).map((_, i) => {
                        const actualIdx = totalItemsInSection <= maxDots
                            ? i
                            : Math.max(0, Math.min(currentSelection.itemIndex - Math.floor(maxDots / 2), totalItemsInSection - maxDots)) + i;
                        const isCurrentDot = actualIdx === currentSelection.itemIndex;
                        const dotItemId = currentSection.items[actualIdx]?.id || `${currentSelection.sectionIndex}-${currentSection.items[actualIdx]?.title}`;
                        const isDone = completedItems.has(dotItemId) || currentSection.status === 'completed';
                        return (
                            <button
                                key={`dot-${actualIdx}`}
                                onClick={() => handleLessonSelect(currentSelection.sectionIndex, actualIdx)}
                                className={`rounded-full transition-all duration-300 ${getDotClass(isCurrentDot, isDone)}`}
                                title={`Lesson ${actualIdx + 1}`}
                            />
                        );
                    })}
                    {totalItemsInSection > maxDots && (
                        <span className="text-[10px] text-gray-400 font-medium ml-1">
                            {currentSelection.itemIndex + 1}/{totalItemsInSection}
                        </span>
                    )}
                </div>

                <button
                    onClick={handleCompleteAndContinue}
                    disabled={(isLastItem && currentSection.status === 'completed') || isMarkingComplete}
                    className={`
                        px-5 py-2.5 rounded-full font-bold text-sm flex items-center gap-2
                        shadow-md transition-all duration-200 active:scale-95
                        ${getNextButtonClass(isMarkingComplete, isSectionLastItem, currentSection.status, isLastItem)}
                    `}
                >
                    {getNextButtonContent(isMarkingComplete, isSectionLastItem, currentSection.status)}
                </button>
            </div>
        </div>
    );
};
BottomNavBar.propTypes = {
    currentSection: PropTypes.object.isRequired,
    currentSelection: PropTypes.object.isRequired,
    completedItems: PropTypes.instanceOf(Set).isRequired,
    totalItemsInSection: PropTypes.number.isRequired,
    isFirstItem: PropTypes.bool.isRequired,
    isLastItem: PropTypes.bool,
    isSectionLastItem: PropTypes.bool,
    isMarkingComplete: PropTypes.bool.isRequired,
    handlePrevious: PropTypes.func.isRequired,
    handleLessonSelect: PropTypes.func.isRequired,
    handleCompleteAndContinue: PropTypes.func.isRequired,
};

/* ─── Lesson Content ─────────────────────────────────────────────────────── */
const LessonContent = ({
    currentItem, currentSection, currentSelection, isEmbedType,
    contentKey, contentScrollRef, completedItems, totalItemsInSection,
    isFirstItem, isLastItem, isSectionLastItem, isMarkingComplete,
    handlePrevious, handleLessonSelect, handleCompleteAndContinue, handleQuizCompletion,
}) => {
    const isQuiz = currentItem.content_type?.toLowerCase() === 'quiz';
    return (
        <>
            {isEmbedType ? (
                <EmbedContent
                    currentItem={currentItem}
                    currentSection={currentSection}
                    currentSelection={currentSelection}
                />
            ) : (
                <InlineContent
                    currentItem={currentItem}
                    currentSection={currentSection}
                    currentSelection={currentSelection}
                    contentKey={contentKey}
                    contentScrollRef={contentScrollRef}
                    handleQuizCompletion={handleQuizCompletion}
                />
            )}
            {!isQuiz && (
                <BottomNavBar
                    currentSection={currentSection}
                    currentSelection={currentSelection}
                    completedItems={completedItems}
                    totalItemsInSection={totalItemsInSection}
                    isFirstItem={isFirstItem}
                    isLastItem={isLastItem}
                    isSectionLastItem={isSectionLastItem}
                    isMarkingComplete={isMarkingComplete}
                    handlePrevious={handlePrevious}
                    handleLessonSelect={handleLessonSelect}
                    handleCompleteAndContinue={handleCompleteAndContinue}
                />
            )}
        </>
    );
};
LessonContent.propTypes = {
    currentItem: PropTypes.object.isRequired,
    currentSection: PropTypes.object.isRequired,
    currentSelection: PropTypes.object.isRequired,
    isEmbedType: PropTypes.bool,
    contentKey: PropTypes.number.isRequired,
    contentScrollRef: PropTypes.object.isRequired,
    completedItems: PropTypes.instanceOf(Set).isRequired,
    totalItemsInSection: PropTypes.number.isRequired,
    isFirstItem: PropTypes.bool.isRequired,
    isLastItem: PropTypes.bool,
    isSectionLastItem: PropTypes.bool,
    isMarkingComplete: PropTypes.bool.isRequired,
    handlePrevious: PropTypes.func.isRequired,
    handleLessonSelect: PropTypes.func.isRequired,
    handleCompleteAndContinue: PropTypes.func.isRequired,
    handleQuizCompletion: PropTypes.func.isRequired,
};

/* ─── State Updater Helpers (extracted to reduce LessonViewer cognitive complexity) ── */
const updateSectionStatus = (prev, sectionIndex) =>
    prev.map((s, idx) => (idx === sectionIndex ? { ...s, status: 'completed' } : s));

const updateProgressCount = (prev) => {
    const completedCount = prev.completed + 1;
    return { ...prev, completed: completedCount, percentage: Math.round((completedCount / prev.total) * 100) };
};

const computeNavigationFlags = (currentSelection, sections) => {
    const { sectionIndex, itemIndex } = currentSelection;
    const currentSection = sections[sectionIndex];
    const currentItem = currentSection?.items?.[itemIndex];
    const isFirstItem = sectionIndex === 0 && itemIndex === 0;
    const isLastItem = Boolean(
        currentSection && currentItem &&
        itemIndex === currentSection.items.length - 1 &&
        sectionIndex === sections.length - 1
    );
    const isSectionLastItem = Boolean(
        currentSection && currentItem &&
        itemIndex === currentSection.items.length - 1
    );
    return { currentSection, currentItem, isFirstItem, isLastItem, isSectionLastItem };
};

/* ─── Main Component ─────────────────────────────────────────────────────── */
const LessonViewer = () => {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const initialLevelId = location.state?.currentLevelId;
    const { setCustomTitle } = useOutletContext() || {};

    const [loading, setLoading] = useState(true);
    const [eventData, setEventData] = useState(null);
    const [sections, setSections] = useState([]);
    const [currentSelection, setCurrentSelection] = useState({ sectionIndex: 0, itemIndex: 0 });
    const [progress, setProgress] = useState({ total: 0, completed: 0, percentage: 0 });
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [expandedSections, setExpandedSections] = useState({});
    const [completedItems, setCompletedItems] = useState(new Set());
    const [isMarkingComplete, setIsMarkingComplete] = useState(false);
    const [contentKey, setContentKey] = useState(0);

    useEffect(() => {
        if (eventData?.name && setCustomTitle) {
            setCustomTitle(eventData.name);
        }
    }, [eventData?.name, setCustomTitle]);

    const fetchLessons = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${serverURL}/lessons/event/${eventId}/all`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const { event, lessons: fetchedSections, progress: fetchedProgress } = response.data;
            setEventData(event);
            setSections(fetchedSections || []);
            setProgress(fetchedProgress);

            const initialExpanded = {};
            (fetchedSections || []).forEach((_, idx) => { initialExpanded[idx] = true; });
            setExpandedSections(initialExpanded);
            setCompletedItems(buildInitialCompletedItems(fetchedSections || []));

            const selection = resolveInitialSelection(fetchedSections || [], initialLevelId);
            if (selection) setCurrentSelection(selection);
        } catch (error) {
            console.error('Error fetching lessons:', error);
            toast.error('Failed to load lessons');
        } finally {
            setLoading(false);
        }
    }, [eventId, initialLevelId]);

    useEffect(() => { fetchLessons(); }, [fetchLessons]);

    const { currentSection, currentItem, isFirstItem, isLastItem, isSectionLastItem } =
        computeNavigationFlags(currentSelection, sections);
    const isEmbedType = currentItem && ['video', 'pdf', 'iframe', 'html'].includes(currentItem.content_type?.toLowerCase());

    const markCurrentItemComplete = useCallback(() => {
        if (!currentItem) return;
        const itemId = currentItem.id || `${currentSelection.sectionIndex}-${currentItem.title}`;
        setCompletedItems(prev => { const next = new Set(prev); next.add(itemId); return next; });
    }, [currentItem, currentSelection.sectionIndex]);

    const handleLessonSelect = (sectionIndex, itemIndex) => {
        setCurrentSelection({ sectionIndex, itemIndex });
        setContentKey(k => k + 1);
        const isMobile = window.innerWidth < 768;
        if (isMobile) setIsSidebarOpen(false);
    };

    const contentScrollRef = useRef(null);
    useEffect(() => {
        if (contentScrollRef.current) contentScrollRef.current.scrollTop = 0;
        setContentKey(k => k + 1);
    }, [currentSelection]);

    const toggleSection = (index) => {
        setExpandedSections(prev => ({ ...prev, [index]: !prev[index] }));
    };

    const navigateNext = () => navigateToNext(currentSelection, sections, setCurrentSelection, setExpandedSections);
    const handlePrevious = () => navigateToPrevious(currentSelection, sections, setCurrentSelection, setExpandedSections);

    const handleMarkSectionComplete = async () => {
        if (!currentSection || currentSection.status === 'completed' || isMarkingComplete) return;
        if (currentSection.is_online_test) return;

        setIsMarkingComplete(true);
        try {
            const token = localStorage.getItem('token');
            await axios.put(
                `${serverURL}/lessons/${currentSection.level_id}/progress`,
                { status: 'completed' },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSections(prev => updateSectionStatus(prev, currentSelection.sectionIndex));
            setProgress(prev => updateProgressCount(prev));
            toast.success('🎉 Section completed!');
        } catch (error) {
            console.error('Error updating progress:', error);
            toast.error('Failed to mark section complete');
        } finally {
            setIsMarkingComplete(false);
        }
    };

    const handleQuizCompletion = async ({ finalStatus } = {}) => {
        await fetchLessons();
        if (finalStatus === 'disqualified') {
            toast.error('Assessment disqualified due to repeated focus loss.');
            // Redirect disqualified candidates to dashboard unconditionally
            setTimeout(() => navigate('/events'), 2000);
            return;
        }
        // If this was the last section and completed successfully, redirect to dashboard
        const isLastSection = currentSelection.sectionIndex === sections.length - 1;
        if (isLastSection) {
            setTimeout(() => navigate('/events'), 2000);
        }
    };

    const handleCompleteAndContinue = async () => {
        if (isSectionLastItem && currentSection.status !== 'completed') {
            await handleMarkSectionComplete();
        }
        markCurrentItemComplete();
        navigateNext();
    };

    if (loading) return <SkeletonLoader />;

    if (!eventData || sections.length === 0) return (
        <div className="flex flex-col items-center justify-center h-screen text-center p-8 bg-gradient-to-br from-gray-50 to-gray-100">
            <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center mb-6 shadow-inner">
                <FaBookOpen size={40} className="text-gray-300" />
            </div>
            <h3 className="text-2xl font-bold text-gray-700 mb-2">No lessons yet</h3>
            <p className="text-gray-400 max-w-xs mb-8">There are no lessons available for this event yet. Check back soon!</p>
            <button
                onClick={() => navigate('/events')}
                className="px-6 py-3 rounded-full bg-gray-900 text-white text-sm font-bold hover:bg-red-600 transition-all duration-200 shadow-lg hover:shadow-red-200 active:scale-95 flex items-center gap-2"
            >
                <FaArrowLeft size={12} /> Dashboard
            </button>
        </div>
    );

    const totalItemsInSection = currentSection?.items?.length || 0;

    return (
        <div className="flex h-screen bg-gray-50 flex-col md:flex-row overflow-hidden">
            {/* ── MOBILE HEADER ── */}
            <div className="md:hidden bg-gray-900 text-white px-4 pt-3 pb-0 flex flex-col z-20 flex-shrink-0 shadow-lg">
                <div className="flex justify-between items-center mb-2">
                    <div className="min-w-0 flex-1 pr-3">
                        <p className="text-[10px] font-semibold text-red-400 uppercase tracking-widest mb-0.5">
                            {currentSection?.level_name || eventData.name}
                        </p>
                        <h1 className="font-bold text-white truncate text-sm leading-tight">
                            {currentItem?.title || eventData.name}
                        </h1>
                    </div>
                    <button
                        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                        className="flex-shrink-0 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all duration-200"
                        aria-label={isSidebarOpen ? 'Close menu' : 'Open menu'}
                    >
                        <span className={`transition-transform duration-300 ${isSidebarOpen ? 'rotate-90' : 'rotate-0'}`}>
                            {isSidebarOpen ? <FaTimes size={15} /> : <FaBars size={15} />}
                        </span>
                    </button>
                </div>
                <div className="w-full bg-white/10 h-0.5 rounded-full overflow-hidden">
                    <div
                        className="h-full bg-gradient-to-r from-red-400 to-green-400 rounded-full transition-all duration-700"
                        style={{ width: `${progress.percentage}%` }}
                    />
                </div>
            </div>

            {/* ── LEFT SIDEBAR ── */}
            <div className={`
                fixed top-[57px] inset-x-0 bottom-0
                md:static md:top-auto md:inset-auto
                z-30 md:w-64 lg:w-72 xl:w-80 w-full flex flex-col
                transition-transform duration-300 transform
                ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
            `}>
                <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex-shrink-0 relative overflow-hidden">
                    <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-red-600/10 blur-2xl pointer-events-none" />
                    <div className="absolute -bottom-4 -left-4 w-24 h-24 rounded-full bg-red-500/5 blur-xl pointer-events-none" />
                    <div className="relative p-5">
                        {/* <button
                            onClick={() => navigate('/events')}
                            className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400 hover:text-white uppercase tracking-widest mb-4 transition-colors group"
                        >
                            <FaChevronLeft size={8} className="group-hover:-translate-x-0.5 transition-transform" />
                            Dashboard
                        </button> */}
                        <div className="flex items-center gap-4">
                            <div className="relative flex-shrink-0">
                                <CircularProgress percentage={progress.percentage} />
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    <span className="text-white font-black text-base leading-none">{Math.round(progress.percentage)}%</span>
                                </div>
                            </div>
                            <div className="min-w-0 flex-1">
                                <h2 className="font-bold text-white text-sm leading-snug line-clamp-3 mb-2">
                                    {eventData.name}
                                </h2>
                                <div className="flex gap-1">
                                    {sections.map((section, i) => (
                                        <div
                                            key={section.level_id}
                                            className={`h-1 rounded-full transition-all duration-500 ${i < progress.completed
                                                ? 'bg-gradient-to-r from-green-400 to-emerald-400 w-4 shadow-[0_0_6px_rgba(74,222,128,0.6)]'
                                                : 'bg-white/25 w-2.5 border border-white/10'
                                                }`}
                                        />
                                    ))}
                                </div>
                                <p className="text-gray-400 text-[10px] mt-1.5 font-medium">
                                    {progress.completed} of {progress.total} module
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sections List */}
                <div className="flex-1 overflow-y-auto custom-scrollbar py-3 bg-gray-50/80">
                    {sections.map((section, sectionIdx) => (
                        <SectionRow
                            key={section.level_id}
                            section={section}
                            sectionIdx={sectionIdx}
                            currentSelection={currentSelection}
                            completedItems={completedItems}
                            expandedSections={expandedSections}
                            onToggle={toggleSection}
                            onSelect={handleLessonSelect}
                        />
                    ))}
                    <div className="h-4" />
                </div>
            </div>

            {/* Mobile Sidebar Overlay */}
            {isSidebarOpen && (
                <button
                    type="button"
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm z-20 md:hidden w-full h-full border-0 p-0 cursor-default"
                    onClick={() => setIsSidebarOpen(false)}
                    aria-label="Close sidebar"
                />
            )}

            {/* ── MAIN CONTENT AREA ── */}
            <div className="flex-1 flex flex-col min-h-0 overflow-hidden bg-white relative">
                {currentItem ? (
                    <LessonContent
                        currentItem={currentItem}
                        currentSection={currentSection}
                        currentSelection={currentSelection}
                        isEmbedType={isEmbedType}
                        contentKey={contentKey}
                        contentScrollRef={contentScrollRef}
                        completedItems={completedItems}
                        totalItemsInSection={totalItemsInSection}
                        isFirstItem={isFirstItem}
                        isLastItem={isLastItem}
                        isSectionLastItem={isSectionLastItem}
                        isMarkingComplete={isMarkingComplete}
                        handlePrevious={handlePrevious}
                        handleLessonSelect={handleLessonSelect}
                        handleCompleteAndContinue={handleCompleteAndContinue}
                        handleQuizCompletion={handleQuizCompletion}
                    />
                ) : (
                    <div className="flex flex-col items-center justify-center h-full bg-gradient-to-br from-gray-50 to-white p-8">
                        <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-5 shadow-inner">
                            <FaBookOpen size={36} className="text-gray-300" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-600 mb-1">Select a lesson to begin</h3>
                        <p className="text-sm text-gray-400 text-center max-w-xs">
                            Choose any lesson from the sidebar to start learning.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LessonViewer;
